import { cellKey } from '@blueprint/domain'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useBoardStore } from '../../state/board-store.js'
import { useUiStore } from '../../state/ui-store.js'
import { parseCellKey } from './VirtualCardList.js'
import styles from './Minimap.module.css'

const BOARD_SELECTOR = '[data-testid="board-scroller"]'
const MAP_WIDTH = 200
const MAP_HEIGHT = 140
const MARGIN = 12

/** A cell's place on the board, in board content pixels. */
interface Block {
  key: string
  x: number
  y: number
  w: number
  h: number
  count: number
}

interface Geometry {
  width: number
  height: number
  blocks: Block[]
}

interface Viewport {
  x: number
  y: number
  w: number
  h: number
}

const EMPTY_GEOMETRY: Geometry = { width: 0, height: 0, blocks: [] }

/**
 * A floating overview of the board with the current viewport drawn on it. Clicking or
 * dragging inside it scrolls the board. It reads the board's own scroll geometry rather
 * than modelling the grid, so it stays correct as rows grow to fit their cards.
 */
export function Minimap() {
  const open = useUiStore((state) => state.minimapOpen)
  const setMinimapOpen = useUiStore((state) => state.setMinimapOpen)
  const [geometry, setGeometry] = useState<Geometry>(EMPTY_GEOMETRY)
  const [viewport, setViewport] = useState<Viewport>({ x: 0, y: 0, w: 0, h: 0 })
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null)
  const mapRef = useRef<HTMLDivElement>(null)

  // Cheap: four numbers, so it can run on every scroll frame.
  const measureViewport = useCallback(() => {
    const board = document.querySelector<HTMLElement>(BOARD_SELECTOR)
    if (!board) return
    setViewport({ x: board.scrollLeft, y: board.scrollTop, w: board.clientWidth, h: board.clientHeight })
  }, [])

  /**
   * Expensive: one rect per cell, and it forces layout. Only runs when the board's size
   * changes, never on scroll, and never while the minimap is closed.
   */
  const measureGeometry = useCallback(() => {
    const board = document.querySelector<HTMLElement>(BOARD_SELECTOR)
    const grid = board?.firstElementChild
    if (!board || !grid) return
    const cards = useBoardStore.getState().entities?.cardIdsByCell
    const gridRect = grid.getBoundingClientRect()
    const blocks: Block[] = []
    for (const cell of grid.querySelectorAll<HTMLElement>('[data-cell]')) {
      const key = cell.dataset.cell ?? ''
      const { rowId, columnId } = parseCellKey(key)
      const count = cards?.[cellKey(rowId, columnId)]?.length ?? 0
      if (count === 0) continue
      const rect = cell.getBoundingClientRect()
      blocks.push({
        key,
        x: rect.left - gridRect.left,
        y: rect.top - gridRect.top,
        w: rect.width,
        h: rect.height,
        count,
      })
    }
    setGeometry({ width: board.scrollWidth, height: board.scrollHeight, blocks })
  }, [])

  useEffect(() => {
    if (!open) return
    const board = document.querySelector<HTMLElement>(BOARD_SELECTOR)
    if (!board) return

    let frame = 0
    const schedule = (measure: () => void) => () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        measure()
      })
    }
    const onScroll = schedule(measureViewport)
    const onResize = schedule(() => {
      measureGeometry()
      measureViewport()
    })

    measureGeometry()
    measureViewport()
    board.addEventListener('scroll', onScroll, { passive: true })
    const observer = new ResizeObserver(onResize)
    observer.observe(board)
    if (board.firstElementChild) observer.observe(board.firstElementChild)
    return () => {
      cancelAnimationFrame(frame)
      board.removeEventListener('scroll', onScroll)
      observer.disconnect()
    }
  }, [open, measureGeometry, measureViewport])

  if (!open) return null

  const scale = geometry.width > 0 && geometry.height > 0
    ? Math.min(MAP_WIDTH / geometry.width, MAP_HEIGHT / geometry.height)
    : 0

  /** Centres the board on the point clicked, clamped by the browser's own scroll bounds. */
  const scrollTo = (event: { clientX: number; clientY: number }) => {
    const map = mapRef.current
    const board = document.querySelector<HTMLElement>(BOARD_SELECTOR)
    if (!map || !board || scale === 0) return
    const rect = map.getBoundingClientRect()
    board.scrollLeft = (event.clientX - rect.left) / scale - board.clientWidth / 2
    board.scrollTop = (event.clientY - rect.top) / scale - board.clientHeight / 2
  }

  const style = position
    ? { left: position.x, top: position.y }
    : { right: MARGIN, bottom: 40 + MARGIN }

  return (
    <aside className={styles.panel} style={style} aria-label="Board minimap" data-testid="minimap">
      <div
        className={styles.header}
        onPointerDown={(event) => {
          if (event.button !== 0) return
          const panel = event.currentTarget.parentElement
          if (!panel) return
          const rect = panel.getBoundingClientRect()
          const grabX = event.clientX - rect.left
          const grabY = event.clientY - rect.top
          event.currentTarget.setPointerCapture(event.pointerId)

          const onMove = (move: PointerEvent) => {
            // Clamped so the panel cannot be dragged out of reach.
            const x = Math.min(Math.max(0, move.clientX - grabX), window.innerWidth - rect.width)
            const y = Math.min(Math.max(0, move.clientY - grabY), window.innerHeight - rect.height)
            setPosition({ x, y })
          }
          const onUp = () => {
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('pointerup', onUp)
          }
          window.addEventListener('pointermove', onMove)
          window.addEventListener('pointerup', onUp)
        }}
      >
        <span className={styles.title}>Minimap</span>
        <button
          type="button"
          className={styles.close}
          aria-label="Hide minimap"
          onClick={() => setMinimapOpen(false)}
        >
          &#10005;
        </button>
      </div>

      <div
        ref={mapRef}
        className={styles.map}
        style={{ width: MAP_WIDTH, height: MAP_HEIGHT }}
        onPointerDown={(event) => {
          if (event.button !== 0) return
          event.currentTarget.setPointerCapture(event.pointerId)
          scrollTo(event)
        }}
        onPointerMove={(event) => {
          // Buttons is a bitmask; 1 means the primary button is still held.
          if (event.buttons & 1) scrollTo(event)
        }}
      >
        {scale === 0 ? (
          <p className={styles.empty}>Nothing to show yet</p>
        ) : (
          <>
            {geometry.blocks.map((block) => (
              <span
                key={block.key}
                className={styles.block}
                style={{
                  left: block.x * scale,
                  top: block.y * scale,
                  width: Math.max(1, block.w * scale),
                  height: Math.max(1, block.h * scale),
                  // Denser cells read darker, so the board's shape is visible at a glance.
                  opacity: Math.min(0.85, 0.3 + block.count * 0.12),
                }}
              />
            ))}
            <span
              className={styles.viewport}
              style={{
                left: viewport.x * scale,
                top: viewport.y * scale,
                width: viewport.w * scale,
                height: viewport.h * scale,
              }}
            />
          </>
        )}
      </div>
    </aside>
  )
}
